// import { useState, useMemo } from "react";
// import { validateField } from "./Validation";

// // Format field names for error messages
// const formatFieldName = (field) => {
//   return field
//     .replace(/([a-z])([A-Z])/g, "$1 $2")
//     .replace(/_/g, " ")
//     .replace(/\b\w/g, (char) => char.toUpperCase());
// };

// const useFormHandler = (initialValues = {}, validationRules = {}) => {
//   const [formData, setFormData] = useState(initialValues);
//   const [errors, setErrors] = useState({});

//   const rulesMemo = useMemo(() => validationRules, [validationRules]);

//   const getRules = (field) => rulesMemo[field] || {};

//   const handleChange = (field, value) => {
//     let updatedValue = value;

//     if (rulesMemo[field]?.type === "checkbox") {
//       updatedValue = Array.isArray(value) ? value : [];
//     }
//     if (rulesMemo[field]?.type === "phone") {
//       updatedValue = value;
//       // console.log("Phone field change:", {
//       //   field,
//       //   value: updatedValue,
//       //   rules: rulesMemo[field],
//       // });
//     }

//     if (rulesMemo[field]?.type === "file") {
//       updatedValue = value instanceof FileList ? Array.from(value) : value;
//     }

//     setFormData((prev) => ({ ...prev, [field]: updatedValue }));

//     // Validate the field
//     validateSingleField(field, updatedValue);
//   };

//   const validateSingleField = (field, value) => {
//     const rules = getRules(field);
//     const fieldName = formatFieldName(field);

//     const error = validateField({
//       type: rules.type || "text",
//       value,
//       values:
//         rules.type === "file" ? (Array.isArray(value) ? value : []) : value,
//       required: rules.required || false,
//       minLength: rules.minLength,
//       maxLength: rules.maxLength,
//       minSelection: rules.minSelection,
//       maxSelection: rules.maxSelection,
//       allowedFileTypes: rules.allowedFileTypes,
//       maxFileSize: rules.maxFileSize,
//       fieldName,
//       customMessages: rules.customMessages || {},
//       number: rules.number || false,
//     });

//     setErrors((prev) => ({ ...prev, [field]: error }));
//   };

//   const validateAllFields = () => {
//     const newErrors = {};
//     let isValid = true;

//     Object.keys(rulesMemo).forEach((field) => {
//       let value = formData[field];

//       if (rulesMemo[field]?.type === "checkbox") {
//         value = Array.isArray(value) ? value : [];
//       }

//       const fieldName = formatFieldName(field);
//       const error = validateField({
//         type: rulesMemo[field]?.type || "text",
//         value,
//         values: value,
//         required: rulesMemo[field]?.required || false,
//         minLength: rulesMemo[field]?.minLength,
//         maxLength: rulesMemo[field]?.maxLength,
//         minSelection: rulesMemo[field]?.minSelection,
//         maxSelection: rulesMemo[field]?.maxSelection,
//         fieldName,
//         customMessages: rulesMemo[field]?.customMessages || {},
//         number: rulesMemo[field]?.number || false,
//       });

//       if (error) {
//         newErrors[field] = error;
//         isValid = false;
//       }
//     });

//     setErrors(newErrors);
//     return isValid;
//   };

//   const resetForm = (newState = initialValues) => {
//     setFormData(newState);
//     setErrors({});
//   };

//   return {
//     formData,
//     errors,
//     handleChange,
//     validateAllFields,
//     setFormData,
//     setErrors,
//     resetForm,
//   };
// };

// export default useFormHandler;
import { useState, useMemo, useEffect } from "react";
import { validateField } from "./Validation";

// Format field names for error messages
const formatFieldName = (field) => {
  // Extract the base field name from paths like locations[0].addressLine1
  const baseName = field.split(".").pop()?.split("[").shift() || field;

  return baseName
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper to get a value from a nested object using a path string
const getNestedValue = (obj, path) => {
  if (!path) return obj;

  const keys = path.replace(/\[(\w+)\]/g, ".$1").split(".");
  let result = obj;

  for (const key of keys) {
    result = result?.[key];
    if (result === undefined) break;
  }

  return result;
};

// Helper to set a value in a nested object using a path string
const setNestedValue = (obj, path, value) => {
  if (!path) return value;

  const result = { ...obj };
  const keys = path.replace(/\[(\w+)\]/g, ".$1").split(".");
  let current = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    // Create a copy of the current level to maintain immutability
    if (Array.isArray(current[key])) {
      current[key] = [...current[key]];
    } else if (typeof current[key] === 'object' && current[key] !== null) {
      current[key] = { ...current[key] };
    } else {
      // If next key is a number, create an array, otherwise an object
      const nextKey = keys[i + 1];
      current[key] = !isNaN(parseInt(nextKey)) ? [] : {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

// Extract array index from a path string (e.g., "attachments[0].field" => 0)
const extractArrayIndex = (path) => {
  const matches = path.match(/\[(\d+)\]/);
  return matches ? parseInt(matches[1]) : null;
};

// Get validation rules for a nested field
const getNestedRules = (rules, path) => {
  if (!path || !rules) return undefined;

  // Handle array notation paths (e.g., "attachments[0].attachmentName")
  const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");
  const parts = normalizedPath.split(".");

  // Find the base array field (e.g., "attachments")
  const baseField = parts[0];

  // Handle array fields (e.g., attachments: [...])
  if (Array.isArray(rules[baseField]) && rules[baseField].length > 0) {
    // For paths like attachments[0].attachmentName
    if (parts.length > 1) {
      // The field name is the last part (e.g., "attachmentName")
      const fieldName = parts[parts.length - 1];
      return rules[baseField][0][fieldName];
    }
  }

  return undefined;
};

const useFormHandler = (initialValues = {}, validationRules = {}) => {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [initialized, setInitialized] = useState(false);

  const rulesMemo = useMemo(() => validationRules, [validationRules]);

  ////Added by Shafi
  useEffect(() => {
    if (!initialized) {
      setFormData(initialValues);
      setInitialized(true);
    }
  }, [JSON.stringify(initialValues)]);
  ////Added by Shafi

  // Main handler for form field changes
  const handleChange = (field, value) => {
    let updatedValue;
    let rules;

    if (!field.includes(".") && !field.includes("[")) {
      rules = rulesMemo[field];
      updatedValue = preprocessValue(value, rules?.type, rules?.multiple, rules?.casing);
      setFormData((prev) => ({ ...prev, [field]: updatedValue }));
    } else {
      rules = getNestedRules(rulesMemo, field);
      updatedValue = preprocessValue(value, rules?.type, rules?.multiple, rules?.casing);
      setFormData((prev) => setNestedValue(prev, field, updatedValue));
    }

    // Validate the changed field
    validateSingleField(field, updatedValue);

    // Validate fields that have notMatchField referencing this field
    Object.keys(rulesMemo).forEach((otherField) => {
      if (!otherField.includes(".") && rulesMemo[otherField]?.notMatchField === field) {
        validateSingleField(otherField, formData[otherField]);
      } else if (otherField.includes(".")) {
        const nestedRules = getNestedRules(rulesMemo, otherField);
        if (nestedRules?.notMatchField === field) {
          validateSingleField(otherField, getNestedValue(formData, otherField));
        }
      }
    });
  };

  // Preprocess values based on field type
  const preprocessValue = (value, fieldType, isMultiple = false, casing = null) => {
    if (fieldType === "checkbox" && !Array.isArray(value)) {
      return [];
    }

    if (fieldType === "file" && value instanceof FileList) {
      return Array.from(value);
    }
    if (fieldType === "datepickerRange") {
      if (!value || typeof value !== "object") {
        return { from: null, to: null };
      }
      return value;
    }
    if (fieldType === "email" && isMultiple) {
      if (!Array.isArray(value)) {
        return [];
      }
      return value;
    }
    if (casing === 'upper') return value.toUpperCase();
    if (casing === 'lower') return value.toLowerCase();

    return value;
  };

  // Validate a single field
  const validateSingleField = (field, value) => {
    let rules;
    let fieldName;

    if (!field.includes(".") && !field.includes("[")) {
      // Regular top-level field
      rules = rulesMemo[field];
      fieldName = formatFieldName(field);
    } else {
      // Nested field
      rules = getNestedRules(rulesMemo, field);
      fieldName = formatFieldName(field);
    }

    if (!rules) return true; // No validation rules for this field


    // Get notMatchField data
    const notMatchField = rules.notMatchField;
    let notMatchFieldValue = null;
    if (notMatchField) {
      notMatchFieldValue = !notMatchField.includes(".")
        ? formData[notMatchField]
        : getNestedValue(formData, notMatchField);
    }







    const error = validateField({
      type: rules.type || "text",
      value,
      values:
        rules.type === "file" ? (Array.isArray(value) ? value : []) : value,
      required: rules.required || false,
      minLength: rules.minLength,
      maxLength: rules.maxLength,
      minSelection: rules.minSelection,
      maxSelection: rules.maxSelection,
      allowedFileTypes: rules.allowedFileTypes,
      maxFileSize: rules.maxFileSize,
      fieldName,
      customMessages: rules.customMessages || {},
      number: rules.number || false,
      numwdec: rules.numwdec || false, //new
      allowedFiles: rules.allowedFiles, //added the new for
      multiple: rules.multiple || false,
      alphanumeric: rules.alphanumeric || false, //added by vinay alphanumeric
      notMatchField: notMatchField ? { field: notMatchField, value: notMatchFieldValue } : null,
      alphaOnly: rules.alphaOnly || false, // Pass new prop


    });

    // Update errors state properly
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      if (!field.includes(".") && !field.includes("[")) {
        // Simple field
        if (error) {
          newErrors[field] = error;
        } else {
          delete newErrors[field];
        }
      } else if (field.includes("[") && field.includes(".")) {
        const baseField = field.split("[")[0];
        const index = extractArrayIndex(field);
        const subField = field.split(".").pop();

        if (error) {
          if (!newErrors[baseField]) newErrors[baseField] = [];
          if (!newErrors[baseField][index]) newErrors[baseField][index] = {};
          newErrors[baseField][index][subField] = error;
        } else if (newErrors[baseField]?.[index]?.[subField]) {
          delete newErrors[baseField][index][subField];
          if (Object.keys(newErrors[baseField][index]).length === 0) {
            newErrors[baseField][index] = undefined;
          }
          if (newErrors[baseField].every((item) => item === undefined)) {
            delete newErrors[baseField];
          }
        }
      }

      return newErrors;
    });

    return !error;
  };

  // Validate all fields in the form
  const validateAllFields = () => {
    let isValid = true;
    const newErrors = {};

    // Validate top-level fields
    Object.keys(rulesMemo).forEach((field) => {
      // Skip array field definitions (they'll be handled separately)
      if (Array.isArray(rulesMemo[field])) return;

      const value = formData[field];
      const rules = rulesMemo[field];
      const fieldName = formatFieldName(field);
      const notMatchField = rules?.notMatchField;
      let notMatchFieldValue = null;
      if (notMatchField) {
        notMatchFieldValue = !notMatchField.includes(".")
          ? formData[notMatchField]
          : getNestedValue(formData, notMatchField);
      }
      const error = validateField({
        type: rules?.type || "text",
        value,
        values:
          rules?.type === "file" ? (Array.isArray(value) ? value : []) : value,
        required: rules?.required || false,
        minLength: rules?.minLength,
        maxLength: rules?.maxLength,
        minSelection: rules?.minSelection,
        maxSelection: rules?.maxSelection,
        allowedFileTypes: rules?.allowedFileTypes,
        maxFileSize: rules?.maxFileSize,
        fieldName,
        customMessages: rules?.customMessages || {},
        number: rules?.number || false,
        numwdec: rules?.numwdec || false, //new
        allowedFiles: rules.allowedFiles,
        multiple: rules?.multiple || false,
        alphanumeric: rules.alphanumeric || false, //added by vinay alphanumeric
        notMatchField: notMatchField ? { field: notMatchField, value: notMatchFieldValue } : null,
        notMatchField: notMatchField ? { field: notMatchField, value: notMatchFieldValue } : null,
      alphaOnly: rules.alphaOnly || false, // Pass new prop

      });

      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // Validate nested array fields
    Object.keys(rulesMemo).forEach((field) => {
      // Only process array validation rules
      if (!Array.isArray(rulesMemo[field]) || !rulesMemo[field].length) return;

      const arrayData = formData[field];
      if (!Array.isArray(arrayData)) return;

      // Validate each item in the array
      arrayData.forEach((item, index) => {
        // Get validation rules for this item's fields
        const itemRules = rulesMemo[field][0];

        // Validate each field in the item
        Object.keys(itemRules).forEach((itemField) => {
          const value = item[itemField];
          const rules = itemRules[itemField];
          const fieldName = formatFieldName(itemField);
          const notMatchField = rules?.notMatchField;
          let notMatchFieldValue = null;
          if (notMatchField) {
            notMatchFieldValue = !notMatchField.includes(".")
              ? formData[notMatchField]
              : getNestedValue(formData, notMatchField);
          }
          const error = validateField({
            type: rules?.type || "text",
            value,
            values:
              rules?.type === "file"
                ? Array.isArray(value)
                  ? value
                  : []
                : value,
            required: rules?.required || false,
            minLength: rules?.minLength,
            maxLength: rules?.maxLength,
            minSelection: rules?.minSelection,
            maxSelection: rules?.maxSelection,
            allowedFileTypes: rules?.allowedFileTypes,
            maxFileSize: rules?.maxFileSize,
            fieldName,
            customMessages: rules?.customMessages || {},
            number: rules?.number || false,
            numwdec: rules?.numwdec || false, //new
            allowedFiles: rules.allowedFiles,
            multiple: rules?.multiple || false,
            alphanumeric: rules.alphanumeric || false, //added by vinay alphanumeric
            notMatchField: notMatchField ? { field: notMatchField, value: notMatchFieldValue } : null,
      alphaOnly: rules.alphaOnly || false, // Pass new prop

          });

          if (error) {
            // Create nested error structure
            if (!newErrors[field]) newErrors[field] = [];
            if (!newErrors[field][index]) newErrors[field][index] = {};
            newErrors[field][index][itemField] = error;
            isValid = false;
          }
        });
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  return {
    formData,
    errors,
    handleChange,
    validateAllFields,
    setFormData,
    setErrors,
  };
};

export default useFormHandler;